import React, { useState, useEffect } from "react";
import { Card, Form, InputGroup, Button } from "react-bootstrap";
import Eye from "../../assets/Eye.svg";
import EyeOff from "../../assets/Eyeoff.svg";
import "./login.css";
import { useForm } from "react-hook-form";
import Google from "../../assets/google-mas 1.svg";
import Facebook from "../../assets/facebook-4.svg";
import { useGoogleLogin } from "@react-oauth/google";
import { useNavigate, useLocation } from "react-router-dom";
import { loginApi, googleUserInfoApi, socialLoginApi, getAppSettings } from "../../api/api";
import { useDispatch, useSelector } from "react-redux";
import { SET_REMEMBER_ME, CLEAR_REMEMBER_ME, LOGIN_SUCCESS } from "../../redux/actions/action";
import { handleError } from "../../helpers/handleError";
import { urlDelPortal } from "../../helpers/zoco";
import brandLogo from "../../assets/image/logo.e3c0b2196cc23f84f67a.png";

const Login = ({ checkout = false }) => {
  const { register, handleSubmit, formState: { errors }, setValue } = useForm();
  const dispatch = useDispatch();
  const rememberMe = useSelector((state) => state.rememberMe);
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = (location && location.state && location.state.from) || "";
  const normalizeRole = (role) => String(role || "").toUpperCase();
  const isOrganizerPanelRole = (role) => ["ORGANIZER", "SELLER"].includes(normalizeRole(role));
  const showCheckoutBanner = checkout || location?.state?.reason === "checkout";
  const [loginError, setLoginError] = useState("");
  const [loginErrorReason, setLoginErrorReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [appSettings, setAppSettings] = useState({});

  // Si venimos desde un evento específico, guarda la orden (draft) como pendingCheckout
  // para poder reanudar apenas se complete el login
  useEffect(() => {
    try {
      if (!fromPath || !/^\/event\//.test(fromPath)) return;
      const m = fromPath.match(/^\/event\/([^/?#]+)/);
      const eid = m && m[1];
      if (!eid) return;
      const raw = localStorage.getItem(`draftOrder:${eid}`);
      if (!raw) return;
      const quantities = JSON.parse(raw);
      if (!quantities || typeof quantities !== 'object' || Object.keys(quantities).length === 0) return;
      try {
        const prev = localStorage.getItem('pendingCheckout');
        if (prev) {
          const data = JSON.parse(prev);
          if (data && data.eventId === eid) return; // ya existe
        }
      } catch (_) {}
      localStorage.setItem('pendingCheckout', JSON.stringify({ provider: 'fiserv', eventId: eid, quantities, ts: Date.now() }));
    } catch (_) {}
  }, [fromPath]);

  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setIsLoading(true);
        // The access token is what proves the identity: the backend resolves it against
        // Google. The profile call below is only for the display name, so a failure there
        // must not block the login.
        let name = "";
        try {
          const response = await googleUserInfoApi(tokenResponse.access_token);
          name = response?.data?.name || "";
        } catch (_) {}
        const data = {
          access_token: tokenResponse.access_token,
          social_type: "google",
          name,
        };
        const response1 = await socialLoginApi(data);

        if (response1.status === 200) {
          const userdata = {
            id: response1.data.info.user._id ?? response1.data.info.user.id,
            email: response1.data.info.user.email,
            name: response1.data.info.user.name,
            role: response1.data.info.user.role,
            dni: response1.data.info.user.dni,
          };
          dispatch({
            type: LOGIN_SUCCESS,
            payload: {
              accessToken: response1.data.info.token,
              refreshToken: response1.data.info.refreshToken,
              user: userdata,
            },
          });

          const redirect = (path, options = {}) => {
            setIsLoading(false);
            navigate(path, { replace: true, ...options });
          };

          const roleUpper = normalizeRole(userdata.role);
          const panelRole = isOrganizerPanelRole(roleUpper);
          const needsDni = roleUpper === "USER" && !userdata.dni;

          try {
            const pending = localStorage.getItem("pendingTransferToken");
            if (pending) {
              localStorage.removeItem("pendingTransferToken");
              redirect(`/transfer/accept?token=${encodeURIComponent(pending)}`);
              return;
            }
          } catch {}
          try {
            const pendingCheckout = localStorage.getItem("pendingCheckout");
            if (pendingCheckout) {
              const data = JSON.parse(pendingCheckout);
              if (data?.eventId && !panelRole) {
                redirect(`/event/${data.eventId}`, { state: { from: fromPath } });
                return;
              }
            }
          } catch {}
          // Redirect back to origin (events, reservas, or any valid app path)
          try {
            if (!panelRole && fromPath && !/^\/(login|register|organizer)/.test(fromPath)) {
              redirect(fromPath);
              return;
            }
          } catch {}

          if (needsDni) {
            const fallback = fromPath && !/^\/(login|register)/.test(fromPath) ? fromPath : "/event";
            redirect(fallback || "/event");
            return;
          }

          redirect(panelRole ? "/organizer" : "/myevents");
          return;
        }
        setIsLoading(false);
      } catch (error) {
        console.error(error);
        setIsLoading(false);
        setLoginError(error?.response?.data?.message || "Algo salió mal");
      }
    },
    onError: () => {
      setLoginError("Error al iniciar sesión");
    },
  });

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      if (!data.rememberMe) {
        dispatch({ type: CLEAR_REMEMBER_ME });
      }
      const response = await loginApi(data);
      if (response.status === 200) {
        if (data.rememberMe) {
          dispatch({ type: SET_REMEMBER_ME, payload: { isRememberMe: true, email: data.email, password: data.password } });
        }

        const userdata = {
          id: response.data.info.user.id,
          email: response.data.info.user.email,
          name: response.data.info.user.name,
          role: response.data.info.user.role,
          dni: response.data.info.user.dni,
        };
        const roleUpper = normalizeRole(userdata.role);
        const panelRole = isOrganizerPanelRole(roleUpper);
        dispatch({
          type: LOGIN_SUCCESS,
          payload: {
            accessToken: response.data.info.token,
            refreshToken: response.data.info.refreshToken,
            user: userdata,
          },
        });
        try {
          const pending = localStorage.getItem("pendingTransferToken");
          if (pending) {
            localStorage.removeItem("pendingTransferToken");
            navigate(`/transfer/accept?token=${encodeURIComponent(pending)}`, { replace: true });
            return;
          }
        } catch {}
        try {
          const pendingCheckout = localStorage.getItem("pendingCheckout");
          if (pendingCheckout) {
            const data = JSON.parse(pendingCheckout);
            if (data?.eventId && !panelRole) {
              navigate(`/event/${data.eventId}`, { replace: true });
              return;
            }
          }
        } catch {}
        // Redirect back to origin (events, reservas, or any valid app path)
        try {
          if (!panelRole && fromPath && !/^\/(login|register|organizer)/.test(fromPath)) {
            navigate(fromPath, { replace: true });
            return;
          }
        } catch {}
        navigate(panelRole ? "/organizer" : "/myevents");
      }
    } catch (error) {
      console.error(error);
      const reason = error?.response?.data?.reason || "";
      setLoginErrorReason(reason);
      setLoginError(error?.response?.data?.message || "Algo salió mal");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (rememberMe && rememberMe.isRememberMe) {
      const { email, password } = rememberMe;
      setValue("email", email);
      setValue("password", password);
      setValue("rememberMe", rememberMe.isRememberMe);
    }
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await getAppSettings();
        if (res.status === 200) setAppSettings(res.data.info || {});
      } catch {}
    })();

    // Facebook SDK
    window.fbAsyncInit = () => {
      window.FB.init({ appId: "1679472942666547", cookie: true, xfbml: true, version: "v11.0" });
    };
    ((d, s, id) => {
      var js, fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      js = d.createElement(s); js.id = id; js.src = "https://connect.facebook.net/en_US/sdk.js"; fjs.parentNode.insertBefore(js, fjs);
    })(document, "script", "facebook-jssdk");
  }, []);

  const handleFacebookLogin = () => {
    window.FB.login((response) => {
      if (response.authResponse) {
        window.FB.api("/me", { fields: "id,name,email,picture,birthday,gender,location" }, async (response) => {
          try {
            setIsLoading(true);
            const data = { email: response.email, social_id: response.id, social_type: "facebook", name: response.name };
            const response1 = await socialLoginApi(data);
            if (response1.status === 200) {
              const userdata = {
                id: response1.data.info.user.id ?? response1.data.info.user._id,
                email: response1.data.info.user.email,
                name: response1.data.info.user.name,
                role: response1.data.info.user.role,
            dni: response1.data.info.user.dni,
          };

              dispatch({
                type: LOGIN_SUCCESS,
                payload: {
                  accessToken: response1.data.info.token,
                refreshToken: response1.data.info.refreshToken,
                user: userdata,
              },
            });
              const redirect = (path, options = {}) => {
                setIsLoading(false);
                navigate(path, { replace: true, ...options });
              };
              const roleUpper = normalizeRole(userdata.role);
              const panelRole = isOrganizerPanelRole(roleUpper);
              const needsDni = roleUpper === "USER" && !userdata.dni;
              try {
                const pending = localStorage.getItem("pendingTransferToken");
                if (pending) {
                  localStorage.removeItem("pendingTransferToken");
                  redirect(`/transfer/accept?token=${encodeURIComponent(pending)}`);
                  return;
                }
              } catch {}
              try {
                const pendingCheckout = localStorage.getItem("pendingCheckout");
                if (pendingCheckout) {
                  const dataCheckout = JSON.parse(pendingCheckout);
                  if (dataCheckout?.eventId && !panelRole) {
                    redirect(`/event/${dataCheckout.eventId}`, { state: { from: fromPath } });
                    return;
                  }
                }
              } catch {}
              try {
                if (!panelRole && fromPath && /^\/event\//.test(fromPath)) {
                  redirect(fromPath);
                  return;
                }
              } catch {}
              if (needsDni) {
                const fallback = fromPath && !/^\/(login|register)/.test(fromPath) ? fromPath : "/event";
                redirect(fallback || "/event");
                return;
              }
              redirect(panelRole ? "/organizer" : "/myevents");
              return;
            }
          } catch (error) {
            handleError(error);
            setLoginError(error?.response?.data?.message || "Something went wrong");
          } finally {
            setIsLoading(false);
          }
        });
      } else {
        setLoginError("El usuario canceló el inicio de sesión o no autorizó completamente.");
      }
    });
  };

  return (
    <>
      <div className="login-container pading150">
        <div className="login-logo-wrap">
          <img
            className="login-logo"
            src={appSettings.logo || brandLogo}
            alt="Zoco"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/event')}
          />
        </div>
         {showCheckoutBanner && (
          <div className="login-info">
            <span className="icon">i</span>
            <div>Necesitás iniciar sesión para completar la compra</div>
          </div>
        )}
        <Card className="login-card">
          <Card.Body style={{ padding: "0px" }}>
            <h2 className="login-title">Inicia sesión en tu cuenta</h2>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Form.Group className="mb-4">
                              {loginError !== "" && (
                <div className="login-alert">
                  <span className="icon" style={{color: "#E53935;"}}>i</span>
                  <div>
                    {loginError}{" "}
                    {loginErrorReason === "organizador_entra_por_zoco" && (
                      <a className="link-btn red" href={urlDelPortal("/entrar")}>Entrá por ZOCO</a>
                    )}
                    {loginErrorReason !== "organizador_entra_por_zoco" && typeof loginError === 'string' && loginError.toLowerCase().includes('cuenta') && (
                      <button type="button" className="link-btn red" onClick={() => navigate('/register', { state: { from: fromPath || (location?.state?.from || '') } })}>Registrate aca</button>
                    )}
                  </div>
                </div>
              )}
                <Form.Control
                  type="email"
                  placeholder="Email"
                  {...register("email", { required: "El correo electrónico es obligatorio" })}
                  className="login-input"
                />
                {errors.email && <p variant="nico" style={{color:"#E53935"}} className="field-error">{errors.email.message}</p>}
              </Form.Group>

              <Form.Group className="mb-3 position-relative">
                <InputGroup className="login-input-group">
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    placeholder="Contraseña"
                    {...register("password", { required: "La contraseña es obligatoria" })}
                    className="login-input-password"
                  />
                  <span onClick={() => setShowPassword(!showPassword)} className="login-eye">
                    <img src={showPassword ? EyeOff : Eye} alt="Mostrar/Ocultar contraseña" width={20} height={20} />
                  </span>
                </InputGroup>
                {errors.password && <p className="field-error">{errors.password.message}</p>}
              </Form.Group>
              <Form.Group className="mb-4">
                <Form.Check className="login-remember" type="checkbox" label="Recordarme" {...register("rememberMe")} />
              </Form.Group>

              <Button type="submit" className="login-button" disabled={isLoading}>
                {isLoading ? "Ingresando..." : "Ingresar"}
              </Button>
            </Form>

            <div className="login-links-row">
              <button type="button" className="link-btn" onClick={() => navigate("/login/forgotpassword", { state: { from: fromPath || (location?.state?.from || '') } })}>Olvidé mi contraseña</button>
              <span className="divider">|</span>
              <span>¿No tenés cuenta? </span>
              <button type="button" className="link-btn" onClick={() => navigate("/register", { state: { from: fromPath || (location?.state?.from || '') } })}>Registrate</button>
            </div>

            <div className="login-links-row">
              <span>¿Sos organizador?</span>
              <a className="link-btn" href={urlDelPortal("/entrar")}>Entrá por ZOCO</a>
            </div>

            <div className="login-divider"></div>
            <span className="login-options">O continuá con</span>

            <Button variant="light" className="login-google-button" onClick={() => login()}>
              <img src={Google} alt="Google" /> Google
            </Button>
            <Button variant="light" className="login-facebook-button" onClick={handleFacebookLogin}>
              <img src={Facebook} alt="Facebook" /> Facebook
            </Button>
          </Card.Body>
        </Card>
      </div>
    </>
  );
};

export default Login;
